// Shared by four custom elements across the booking pages: <booking-calendar>
// (book new appointments), <manage-booking> (reschedule/cancel via the
// emailed token link), <regular-request-form> (request a recurring slot, or
// cancel a single date of an existing one), and <account-bookings>
// (logged-in customers' "My Bookings" list). Talks only to the Supabase Edge
// Functions for availability/holds/lookups; cart + payment stay entirely on
// Shopify's own /cart and /checkout.

function fmtDate(d) {
  // Local date components, not toISOString() -- converting to UTC first
  // would show the wrong calendar date for roughly the first hour after
  // midnight BST, since UK local time is ahead of UTC.
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fmtTime(t) {
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h12}${ampm}` : `${h12}:${String(m).padStart(2, '0')}${ampm}`;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const REQUEST_STATUS_LABEL = { pending: 'Pending review', approved: 'Approved', declined: 'Declined' };
const REFUND_STATUS_MESSAGE = {
  pending: 'A refund request has been submitted and is awaiting review by the pool -- you do not need to do anything else.',
  approved: 'Your refund has been approved and processed by the pool.',
  declined: 'The pool has reviewed this cancellation and did not approve a refund. Contact the pool directly if you have questions.',
};

function renderSlotGrid(container, slots, selectedTimes, onPick) {
  container.innerHTML = '';
  if (!slots.length) {
    container.innerHTML = '<p class="booking-calendar__empty">No sessions run on this day.</p>';
    return;
  }
  const list = document.createElement('div');
  list.className = 'booking-calendar__slot-grid';
  for (const slot of slots) {
    const btn = document.createElement('button');
    btn.type = 'button';
    // Deliberately NOT using Dawn's .button/.button--secondary classes here --
    // they carry hover/focus pseudo-elements that were rendering as stray
    // marks between adjacent slot buttons in a tight grid. Fully self-styled
    // instead, so there's nothing inherited to fight with.
    btn.className = 'booking-calendar__slot';
    const isSelected = selectedTimes.has(slot.start_time);
    btn.textContent = (isSelected ? '✓ ' : '') + fmtTime(slot.start_time);
    if (isSelected) btn.classList.add('is-selected');
    if (!slot.available && !isSelected) {
      btn.disabled = true;
      btn.classList.add('is-unavailable');
    } else {
      btn.addEventListener('click', () => onPick(slot.start_time));
    }
    list.appendChild(btn);
  }
  container.appendChild(list);
}

class BookingCalendar extends HTMLElement {
  constructor() {
    super();
    this.supabaseUrl = this.dataset.supabaseUrl;
    this.selected = []; // [{date, start_time}]
    this.price = 0;
  }

  connectedCallback() {
    this.dateInput = this.querySelector('#BookingDate');
    this.slotsEl = this.querySelector('.booking-calendar__slots');
    this.emptySummaryEl = this.querySelector('.booking-calendar__empty-summary');
    this.selectedWrap = this.querySelector('.booking-calendar__selected');
    this.selectedList = this.querySelector('.booking-calendar__selected-list');
    this.totalEl = this.querySelector('.booking-calendar__total');
    this.form = this.querySelector('.booking-calendar__contact');
    this.submitBtn = this.querySelector('.booking-calendar__submit');
    this.errorEl = this.querySelector('.booking-calendar__error');
    this.disclaimerCheck = this.querySelector('#BookingDisclaimerAgree');
    this.disclaimerError = this.querySelector('.booking-calendar__disclaimer-error');

    const today = fmtDate(new Date());
    this.dateInput.min = today;
    this.dateInput.value = today;
    this.dateInput.addEventListener('change', () => this.loadAvailability(this.dateInput.value));
    this.form.addEventListener('submit', (e) => this.onSubmit(e));

    if (this.disclaimerCheck) {
      this.disclaimerCheck.addEventListener('change', () => {
        if (this.disclaimerCheck.checked && this.disclaimerError) this.disclaimerError.hidden = true;
      });
    }

    this.setupPolicyModal();
    this.loadAvailability(today);
  }

  setupPolicyModal() {
    const modal = this.querySelector('#BookingPolicyModal');
    if (!modal) return;
    this.querySelectorAll('[data-action="open-policy-modal"]').forEach((btn) =>
      btn.addEventListener('click', () => modal.showModal())
    );
    modal.querySelectorAll('[data-action="close-policy-modal"]').forEach((btn) =>
      btn.addEventListener('click', () => modal.close())
    );
    modal.addEventListener('click', (e) => {
      if (e.target === modal) modal.close();
    });
  }

  async loadAvailability(date) {
    this.slotsEl.innerHTML = '<p>Loading…</p>';
    try {
      const res = await fetch(`${this.supabaseUrl}/availability?date=${date}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not load availability');
      this.price = data.price;
      const selectedTimes = new Set(this.selected.filter((s) => s.date === date).map((s) => s.start_time));
      renderSlotGrid(this.slotsEl, data.slots, selectedTimes, (start_time) => this.toggleSlot(date, start_time));
    } catch (err) {
      this.slotsEl.innerHTML = `<p class="booking-calendar__error">${err.message}</p>`;
    }
  }

  toggleSlot(date, start_time) {
    const idx = this.selected.findIndex((s) => s.date === date && s.start_time === start_time);
    if (idx > -1) {
      this.selected.splice(idx, 1);
    } else {
      this.selected.push({ date, start_time });
    }
    this.loadAvailability(this.dateInput.value);
    this.renderSelected();
  }

  renderSelected() {
    const has = this.selected.length > 0;
    this.emptySummaryEl.hidden = has;
    this.selectedWrap.hidden = !has;
    this.form.hidden = !has;
    this.selectedList.innerHTML = '';
    for (const s of this.selected) {
      const li = document.createElement('li');
      li.className = 'booking-calendar__selected-item';
      const label = document.createElement('span');
      label.textContent = `${s.date} — ${fmtTime(s.start_time)}`;
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'link';
      remove.textContent = 'Remove';
      remove.addEventListener('click', () => this.toggleSlot(s.date, s.start_time));
      li.append(label, remove);
      this.selectedList.appendChild(li);
    }
    if (has) {
      const total = (this.price * this.selected.length).toFixed(2);
      this.totalEl.textContent = `${this.selected.length} appointment${this.selected.length > 1 ? 's' : ''} — £${total} total, charged as separate line items at checkout`;
    }
  }

  setBusy(busy) {
    this.submitBtn.disabled = busy;
    this.submitBtn.querySelector('.loading-overlay__spinner').classList.toggle('hidden', !busy);
  }

  showError(message) {
    this.errorEl.textContent = message;
    this.errorEl.hidden = !message;
  }

  async onSubmit(event) {
    event.preventDefault();
    this.showError('');
    if (this.selected.length === 0) return;

    if (this.disclaimerCheck && !this.disclaimerCheck.checked) {
      if (this.disclaimerError) this.disclaimerError.hidden = false;
      this.disclaimerCheck.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this.disclaimerCheck.focus();
      return;
    }

    this.setBusy(true);
    try {
      const customer = {
        name: this.querySelector('#BookingName').value.trim(),
        email: this.querySelector('#BookingEmail').value.trim(),
        phone: this.querySelector('#BookingPhone').value.trim(),
      };

      const holdRes = await fetch(`${this.supabaseUrl}/hold`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slots: this.selected, customer }),
      });
      const holdData = await holdRes.json();
      if (!holdRes.ok) {
        if (holdRes.status === 409) {
          this.showError(holdData.message);
          this.selected = this.selected.filter((s) => !(s.date === holdData.slot.date && s.start_time === holdData.slot.start_time));
          this.loadAvailability(this.dateInput.value);
          this.renderSelected();
        } else {
          this.showError(holdData.message || 'Something went wrong -- please try again.');
        }
        return;
      }

      // One cart line per appointment so each is its own line item/price.
      // All of them go in a SINGLE /cart/add.js call -- looping separate
      // sequential calls here previously lost items (Shopify's cart didn't
      // reliably end up with one line per call), and a single batched
      // request is what the Ajax Cart API is actually designed for.
      // Shopify's default order confirmation email renders line item
      // properties automatically, so putting the manage-booking link in a
      // property gets it to the customer without a custom email template.
      const previewParam = new URLSearchParams(window.location.search).get('preview_theme_id');
      const previewSuffix = previewParam ? `&preview_theme_id=${previewParam}` : '';

      const items = holdData.holds.map((hold) => ({
        id: holdData.variant_id,
        quantity: 1,
        properties: {
          Date: hold.date,
          Time: fmtTime(hold.start_time),
          'Booking ID': hold.booking_id,
          'Manage or reschedule': `${window.location.origin}/pages/manage-booking?token=${hold.manage_token}${previewSuffix}`,
        },
      }));

      const addRes = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!addRes.ok) {
        throw new Error('Your appointments are held, but adding them to the cart failed. Please contact the pool before trying again.');
      }
      const addData = await addRes.json();
      const addedCount = Array.isArray(addData.items) ? addData.items.length : 1;
      if (addedCount !== items.length) {
        throw new Error(`Only ${addedCount} of ${items.length} appointments made it into your cart -- please check your cart before checking out.`);
      }

      window.location.href = '/checkout';
    } catch (err) {
      this.showError(err.message);
    } finally {
      this.setBusy(false);
    }
  }
}

customElements.define('booking-calendar', BookingCalendar);

class ManageBooking extends HTMLElement {
  constructor() {
    super();
    this.supabaseUrl = this.dataset.supabaseUrl;
    this.token = new URLSearchParams(window.location.search).get('token');
  }

  connectedCallback() {
    this.statusEl = this.querySelector('.manage-booking__status');
    this.actionsEl = this.querySelector('.manage-booking__actions');
    this.rescheduleEl = this.querySelector('.manage-booking__reschedule');

    if (!this.token) {
      this.statusEl.textContent = "This link is missing its booking reference -- please use the link from your confirmation email.";
      return;
    }
    this.load();
  }

  async load() {
    const res = await fetch(`${this.supabaseUrl}/manage-booking?token=${this.token}`);
    const data = await res.json();
    if (!res.ok) {
      this.statusEl.textContent = data.error || 'Booking not found.';
      return;
    }
    this.booking = data.booking;
    this.canModify = data.can_modify;
    this.refundStatus = data.refund_status;
    this.render();
  }

  render() {
    const b = this.booking;
    this.statusEl.innerHTML = `<p><strong>${fmtTime(b.start_time)} on ${b.date}</strong> -- status: ${b.status}</p>`;

    if (b.status !== 'confirmed') {
      if (b.status === 'cancelled' && REFUND_STATUS_MESSAGE[this.refundStatus]) {
        this.statusEl.innerHTML += `<p>${REFUND_STATUS_MESSAGE[this.refundStatus]}</p>`;
      }
      this.actionsEl.hidden = true;
      return;
    }
    if (!this.canModify) {
      this.statusEl.innerHTML += `<p>Changes need at least 48 hours' notice -- please call the pool directly to sort this one out.</p>`;
      this.actionsEl.hidden = true;
      return;
    }

    this.actionsEl.hidden = false;
    // Self-styled buttons, not Dawn's .button/.button--secondary -- same
    // pseudo-element stray-mark issue fixed elsewhere on this build.
    this.actionsEl.innerHTML = `
      <button type="button" class="manage-booking__button manage-booking__button--primary" data-action="show-reschedule">Reschedule</button>
      <button type="button" class="manage-booking__button manage-booking__button--danger" data-action="cancel">Cancel booking</button>
    `;
    this.actionsEl.querySelector('[data-action="show-reschedule"]').addEventListener('click', () => this.showReschedule());
    this.actionsEl.querySelector('[data-action="cancel"]').addEventListener('click', () => this.cancel());
  }

  showReschedule() {
    this.rescheduleEl.hidden = false;
    this.rescheduleEl.innerHTML = `
      <label class="form__label" for="RescheduleDate">New date</label>
      <input type="date" id="RescheduleDate" min="${fmtDate(new Date())}">
      <div class="manage-booking__slots"></div>
    `;
    const dateInput = this.rescheduleEl.querySelector('#RescheduleDate');
    const slotsEl = this.rescheduleEl.querySelector('.manage-booking__slots');
    dateInput.value = this.booking.date;
    const load = async () => {
      const res = await fetch(`${this.supabaseUrl}/availability?date=${dateInput.value}&service_id=${this.booking.service_id}`);
      const data = await res.json();
      renderSlotGrid(slotsEl, data.slots, new Set(), (start_time) => this.reschedule(dateInput.value, start_time));
    };
    dateInput.addEventListener('change', load);
    load();
  }

  async reschedule(new_date, new_start_time) {
    const res = await fetch(`${this.supabaseUrl}/manage-booking`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: this.token, action: 'reschedule', new_date, new_start_time }),
    });
    const data = await res.json();
    if (!res.ok) {
      this.statusEl.innerHTML += `<p class="manage-booking__error">${data.message || data.error}</p>`;
      return;
    }
    window.location.href = `${window.location.pathname}?token=${data.booking.manage_token}`;
  }

  async cancel() {
    if (!window.confirm('Cancel this booking? If you paid online, this will submit a refund request for the pool to review and approve -- refunds are not automatic.')) return;
    const res = await fetch(`${this.supabaseUrl}/manage-booking`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: this.token, action: 'cancel' }),
    });
    if (res.ok) this.load();
  }
}

customElements.define('manage-booking', ManageBooking);

// Customer-facing forms, both inside the <regular-request-form> element:
// (1) request a recurring ("regular") slot, (2) an existing regular
// cancelling one date. Each form is collapsed behind its own toggle button
// by default to keep the booking page from feeling heavy. Neither touches
// availability directly -- staff review and approve/decline every request
// on the admin calendar (a new regular permanently reserves capacity; a
// cancel request needs a human to match the customer's freeform day/time to
// the actual recurring_blocks row before it can free the slot).
class RegularRequestForm extends HTMLElement {
  constructor() {
    super();
    this.supabaseUrl = this.dataset.supabaseUrl;
  }

  connectedCallback() {
    this.querySelectorAll('[data-toggle-target]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const form = this.querySelector(`[data-form="${btn.dataset.toggleTarget}"]`);
        form.hidden = false;
        btn.hidden = true;
        form.querySelector('input, select, textarea')?.focus();
      });
    });

    const newForm = this.querySelector('[data-form="new"]');
    if (newForm) newForm.addEventListener('submit', (e) => this.onSubmitNewRegular(e, newForm));

    const cancelForm = this.querySelector('[data-form="cancel"]');
    if (cancelForm) cancelForm.addEventListener('submit', (e) => this.onSubmitCancel(e, cancelForm));
  }

  setBusy(form, busy) {
    const btn = form.querySelector('.regular-request__submit');
    btn.disabled = busy;
    btn.querySelector('.loading-overlay__spinner').classList.toggle('hidden', !busy);
  }

  showError(form, message) {
    const el = form.querySelector('.regular-request__error');
    el.textContent = message;
    el.hidden = !message;
  }

  async onSubmitNewRegular(event, form) {
    event.preventDefault();
    this.showError(form, '');
    this.setBusy(form, true);
    try {
      const payload = {
        customer_name: form.querySelector('#RegularName').value.trim(),
        email: form.querySelector('#RegularEmail').value.trim(),
        phone: form.querySelector('#RegularPhone').value.trim(),
        day_of_week: Number(form.querySelector('#RegularDay').value),
        start_time: form.querySelector('#RegularTime').value,
        frequency: form.querySelector('#RegularFrequency').value,
        preferred_start_date: form.querySelector('#RegularStartDate').value || null,
        notes: form.querySelector('#RegularNotes').value.trim() || null,
      };

      const res = await fetch(`${this.supabaseUrl}/request-regular`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Something went wrong -- please try again.');

      form.hidden = true;
      form.querySelector('.regular-request__success').hidden = false;
    } catch (err) {
      this.showError(form, err.message);
    } finally {
      this.setBusy(form, false);
    }
  }

  async onSubmitCancel(event, form) {
    event.preventDefault();
    this.showError(form, '');
    this.setBusy(form, true);
    try {
      const payload = {
        customer_name: form.querySelector('#CancelName').value.trim(),
        email: form.querySelector('#CancelEmail').value.trim(),
        phone: form.querySelector('#CancelPhone').value.trim(),
        day_of_week: Number(form.querySelector('#CancelDay').value),
        start_time: form.querySelector('#CancelTime').value,
        skip_date: form.querySelector('#CancelDate').value,
        reason: form.querySelector('#CancelReason').value.trim() || null,
      };

      const res = await fetch(`${this.supabaseUrl}/request-skip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Something went wrong -- please try again.');

      form.hidden = true;
      form.querySelector('.regular-request__success').hidden = false;
    } catch (err) {
      this.showError(form, err.message);
    } finally {
      this.setBusy(form, false);
    }
  }
}

customElements.define('regular-request-form', RegularRequestForm);

// Logged-in customer account page: lists their bookings + regular-slot
// request statuses. Purely a read/discovery layer -- reschedule and cancel
// both hand off to the existing token-based manage-booking page rather than
// duplicating that logic here.
class AccountBookings extends HTMLElement {
  constructor() {
    super();
    this.supabaseUrl = this.dataset.supabaseUrl;
    this.email = this.dataset.customerEmail;
  }

  connectedCallback() {
    this.listEl = this.querySelector('.account-bookings__list');
    if (!this.email) {
      this.listEl.innerHTML = '<p>We couldn&rsquo;t find an email on your account.</p>';
      return;
    }
    this.load();
  }

  async load() {
    try {
      const res = await fetch(`${this.supabaseUrl}/account-bookings?email=${encodeURIComponent(this.email)}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not load your bookings.');
      this.render(data);
    } catch (err) {
      this.listEl.innerHTML = `<p class="account-bookings__error">${err.message}</p>`;
    }
  }

  render(data) {
    const upcoming = data.bookings.filter((b) => b.status === 'confirmed' && b.upcoming);
    const past = data.bookings.filter((b) => !(b.status === 'confirmed' && b.upcoming));

    const bookingRow = (b) => {
      const isUpcomingConfirmed = b.status === 'confirmed' && b.upcoming;
      return `
      <li class="account-bookings__item${isUpcomingConfirmed ? '' : ' account-bookings__item--past'}">
        <div class="account-bookings__item-main">
          <span class="account-bookings__item-date">${b.date} — ${fmtTime(b.start_time)}</span>
          <span class="account-bookings__badge account-bookings__badge--${b.status}">${b.status === 'confirmed' ? 'Confirmed' : 'Cancelled'}</span>
        </div>
        ${
          isUpcomingConfirmed
            ? b.can_modify
              ? `<div class="account-bookings__item-actions"><a class="account-bookings__link" href="/pages/manage-booking?token=${b.manage_token}">Manage booking &rarr;</a></div>`
              : `<p class="account-bookings__note">Changes need at least ${data.notice_hours} hours' notice -- please contact us directly.</p>`
            : ''
        }
      </li>`;
    };

    const requestRow = (r) => `
      <li class="account-bookings__item">
        <div class="account-bookings__item-main">
          <span class="account-bookings__item-date">${DAY_NAMES[r.day_of_week]} ${fmtTime(r.start_time.slice(0, 5))} · ${r.frequency.replace('_', ' ')}</span>
          <span class="account-bookings__badge account-bookings__badge--${r.status}">${REQUEST_STATUS_LABEL[r.status]}</span>
        </div>
      </li>`;

    this.listEl.innerHTML = `
      <div class="account-bookings__group">
        <h3>Upcoming</h3>
        ${upcoming.length ? `<ul class="account-bookings__items">${upcoming.map(bookingRow).join('')}</ul>` : '<p class="account-bookings__empty">No upcoming bookings.</p>'}
      </div>
      ${
        past.length
          ? `<div class="account-bookings__group"><h3>Past</h3><ul class="account-bookings__items">${past.map(bookingRow).join('')}</ul></div>`
          : ''
      }
      ${
        data.regular_requests.length
          ? `<div class="account-bookings__group"><h3>Regular slot requests</h3><ul class="account-bookings__items">${data.regular_requests.map(requestRow).join('')}</ul></div>`
          : ''
      }
    `;
  }
}

customElements.define('account-bookings', AccountBookings);
